import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Button, Stack } from '@mui/material'
import { addUser } from 'api/users'
import FormInput from 'components/form-elements/FormInput'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, minButtonWidth } from 'const'
import { UIModal } from 'features/UI'
import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { object, string, TypeOf } from 'zod'

interface Props {
  isOpen: boolean
  onClose: () => void
  onAfterSubmit: () => void
  organizationId?: number
}

const formSchema = object({
  firstname: string().min(1, FIELD_IS_REQUIRED),
  lastname: string().min(1, FIELD_IS_REQUIRED),
  email: string().min(1, FIELD_IS_REQUIRED).email(EMAIL_IS_INVALID),
})

type FormType = TypeOf<typeof formSchema>

function AddUserModalContent({ onClose, onAfterSubmit, organizationId }: Props) {
  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
    },
  })
  const { handleSubmit } = methods

  const addUserMutation = useMutation(addUser, {
    onSuccess: () => {
      onClose()
      NotificationSys.showSuccess('User created successfully.')
    },
  })

  const onSubmitHandler = useCallback(
    async (values: FormType) => {
      await addUserMutation.mutateAsync({
        name: values.firstname,
        lastName: values.lastname,
        email: values.email,
        organizationId: organizationId,
        shouldSendWelcomeEmail: false,
      })
      onAfterSubmit()
    },
    [addUserMutation, onAfterSubmit, organizationId],
  )

  return (
    <FormProvider {...methods}>
      <Stack component="form" onSubmit={handleSubmit(onSubmitHandler)} noValidate spacing={3}>
        <FormInput label="First Name" name="firstname" fullWidth />
        <FormInput label="Last Name" name="lastname" fullWidth />
        <FormInput label="Email" name="email" fullWidth />

        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
          <Button variant="outlined" onClick={onClose} sx={minButtonWidth}>
            Close
          </Button>
          <LoadingButton
            loading={addUserMutation.isLoading}
            disabled={addUserMutation.isLoading}
            variant="contained"
            type="submit"
            sx={minButtonWidth}
          >
            Create
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  )
}

export function UserOrgRolesAddUserModal(props: Props) {
  const { isOpen, onClose } = props

  return (
    <UIModal isOpen={isOpen} title="Create User" onClose={onClose}>
      <AddUserModalContent {...props} />
    </UIModal>
  )
}
